import { useId, useMemo, useRef, useState, type CSSProperties, type HTMLAttributes, type Key } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import type {
  FreyrRecipientBucket,
  FreyrRecipientOption,
} from "../../utils/manualAssessmentReportShare";

const LABEL_SX = {
  color: "rgba(54, 65, 83, 1)",
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: "14px",
  lineHeight: "20px",
  fontWeight: 400,
  mb: 0.5,
} as const;

const FIELD_SX = {
  "& .MuiInputBase-root": {
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: 14,
    flexWrap: "nowrap",
  },
  "& .MuiChip-label": {
    textTransform: "none",
  },
} as const;

const GROUP_LABELS: Record<FreyrRecipientBucket, string> = {
  taskUsers: "Users",
  taskGroups: "User Groups",
  taskRoles: "Roles",
};

const GROUP_ORDER: readonly FreyrRecipientBucket[] = [
  "taskUsers",
  "taskGroups",
  "taskRoles",
];

const filterOptions = createFilterOptions<FreyrRecipientOption>({
  stringify: (option) =>
    [option.label, option.email].filter(Boolean).join(" "),
});

export type ShareAssessmentRecipientsSelectProps = Readonly<{
  label: string;
  placeholder?: string;
  options: FreyrRecipientOption[];
  value: FreyrRecipientOption[];
  loading: boolean;
  disabled: boolean;
  onChange: (next: FreyrRecipientOption[]) => void;
}>;

function optionKey(option: FreyrRecipientOption): string {
  return `${option.bucket}-${option.id}`;
}

export function ShareAssessmentRecipientsSelect({
  label,
  placeholder = "Add people, groups...",
  options,
  value,
  loading,
  disabled,
  onChange,
}: ShareAssessmentRecipientsSelectProps) {
  const inputId = useId();
  const groupKeys = useMemo(
    () => GROUP_ORDER.filter((bucket) => options.some((option) => option.bucket === bucket)),
    [options],
  );
  const [activeGroup, setActiveGroup] = useState<FreyrRecipientBucket>("taskUsers");
  const resolvedGroup = groupKeys.includes(activeGroup)
    ? activeGroup
    : (groupKeys[0] ?? "taskUsers");

  const filteredOptions = useMemo(
    () => options.filter((option) => option.bucket === resolvedGroup),
    [options, resolvedGroup],
  );

  const tabMetaRef = useRef({
    activeGroup: resolvedGroup,
    groupKeys,
  });
  tabMetaRef.current = { activeGroup: resolvedGroup, groupKeys };

  const PaperComponent = useMemo(() => {
    return function RecipientsPaper(paperProps: HTMLAttributes<HTMLElement>) {
      const { children, ...rest } = paperProps;
      const { activeGroup: group, groupKeys: keys } = tabMetaRef.current;
      return (
        <Paper elevation={6} {...rest}>
          {keys.length > 0 ? (
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                backgroundColor: "#fff",
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <Tabs
                value={group}
                onChange={(_event, next: FreyrRecipientBucket) => {
                  setActiveGroup(next);
                }}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    margin: 0,
                    padding: "0 10px",
                    minWidth: "auto",
                    textTransform: "none",
                  },
                }}
              >
                {keys.map((bucket) => (
                  <Tab
                    key={bucket}
                    value={bucket}
                    label={GROUP_LABELS[bucket]}
                  />
                ))}
              </Tabs>
            </Box>
          ) : null}
          {children}
        </Paper>
      );
    };
  }, []);

  const selectedTooltip = value.map((option) => option.label).join(", ");

  return (
    <Box>
      <FormLabel htmlFor={inputId} sx={LABEL_SX}>
        {label}
      </FormLabel>
      <Autocomplete
        id={inputId}
        multiple
        disableCloseOnSelect
        options={filteredOptions}
        filterOptions={filterOptions}
        loading={loading}
        value={value}
        disabled={disabled}
        PaperComponent={PaperComponent}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) =>
          option.id === selected.id && option.bucket === selected.bucket
        }
        onChange={(_event, next) => onChange(next)}
        renderTags={(selected) => {
          if (selected.length === 0) return null;
          const chipLabel =
            selected.length === 1
              ? selected[0]?.label
              : `${selected.length} selected`;
          return (
            <Tooltip title={selectedTooltip} placement="top">
              <Chip size="small" label={chipLabel} />
            </Tooltip>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key: _key, ...liProps } = props as typeof props & {
            key?: Key;
          };
          return (
            <li
              {...liProps}
              key={optionKey(option)}
              style={{
                ...(liProps.style as CSSProperties | undefined),
                padding: "8px 12px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Checkbox
                checked={selected}
                size="small"
                sx={{ mr: 1, padding: 0, mt: 0.25 }}
              />
              <Box
                sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.label}
                </Box>
                {option.email ? (
                  <Box
                    component="span"
                    sx={{
                      color: "rgba(0,0,0,0.6)",
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.email}
                  </Box>
                ) : null}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? placeholder : undefined}
            size="small"
            sx={FIELD_SX}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
